import type {RefObject} from 'react';
import {useEffect, useRef} from 'react';

interface UseF6WindowFocusOptions {
  /** Whether the window is open and shown. */
  isRendered: boolean;
  /** Whether F6 moves focus at all; off while the window is modal. */
  enabled: boolean;
  windowRef: RefObject<HTMLDivElement | null>;
  openerRef: RefObject<Element | null>;
}

/**
 * F6 and Shift+F6 move focus between the calculator window and the page. From
 * the page, focus goes to the window. From the window, it returns to the last
 * element focused in the page, or to the element that opened the window.
 */
export const useF6WindowFocus = ({
  isRendered,
  enabled,
  windowRef,
  openerRef
}: UseF6WindowFocusOptions): void => {
  const lastAppFocusRef = useRef<Element | null>(null);

  // The window is portaled after #app, so Tab cannot cross between it and the
  // question; this remembers where focus was in the page.
  useEffect(() => {
    if (!isRendered) {
      return;
    }
    const appRoot = document.getElementById('app');
    const handleFocusIn = (event: FocusEvent) => {
      if (event.target instanceof Element && appRoot?.contains(event.target)) {
        lastAppFocusRef.current = event.target;
      }
    };
    document.addEventListener('focusin', handleFocusIn);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      lastAppFocusRef.current = null;
    };
  }, [isRendered]);

  useEffect(() => {
    if (!isRendered || !enabled) {
      return;
    }
    const handleF6 = (event: KeyboardEvent) => {
      const windowElement = windowRef.current;
      if (
        event.key !== 'F6' ||
        event.ctrlKey ||
        event.altKey ||
        event.metaKey ||
        event.defaultPrevented ||
        !windowElement
      ) {
        return;
      }
      if (!windowElement.contains(document.activeElement)) {
        event.preventDefault();
        windowElement.focus();
        return;
      }
      for (const element of [lastAppFocusRef.current, openerRef.current]) {
        if (!(element instanceof HTMLElement) || element === document.body) continue;
        element.focus();
        if (document.activeElement === element) {
          event.preventDefault();
          return;
        }
      }
    };
    document.addEventListener('keydown', handleF6, true);
    return () => document.removeEventListener('keydown', handleF6, true);
  }, [isRendered, enabled, windowRef, openerRef]);
};
